/**
 * Модуль для работы с профессиями и улучшениями существ
 */

/**
 * Форматирует название характеристики для отображения
 */
function formatStatName(stat) {
  const statNames = {
    happiness: "Счастье",
    curiosity: "Любопытство",
    confidence: "Уверенность",
    sociability: "Общительность",
    empathy: "Эмпатия",
  };
  return statNames[stat] || stat;
}

/**
 * Форматирует метку статистики профессии
 */
function formatProfessionStatLabel(stat) {
  const statLabels = {
    attack: "Атака",
    defense: "Защита",
    speed: "Скорость",
    hp: "HP",
    special: "Особое",
  };
  return statLabels[stat] || stat;
}

/**
 * Создаёт менеджер для работы с профессиями и улучшениями
 */
export function createProfessionsUpgradesManager({
  safeFetch,
  buildAuthHeaders,
  getApiUrl,
  getTelegramWebApp,
  getCurrentUser,
  setCurrentUser,
  updateUserBalancesUI,
  fetchProfessionsData,
  getProfessionStatLabel = formatProfessionStatLabel,
  getUpgradeMethods = null, // Функция для получения методов улучшения (M из legacy-app)
  showScreen,
  documentRef = document,
}) {
  /**
   * Показывает экран выбора профессии для существа
   */
  async function showProfessions(creatureId) {
    const telegramWebApp = getTelegramWebApp();
    try {
      const professionsData = await fetchProfessionsData();
      if (!professionsData) {
        if (telegramWebApp?.showAlert) {
          telegramWebApp.showAlert("Не удалось загрузить список профессий");
        }
        return;
      }

      documentRef
        .querySelectorAll(".screen")
        .forEach((el) => el.classList.remove("active"));
      const screen = documentRef.getElementById("professions-screen");
      if (screen) {
        screen.classList.add("active");
      }

      const domCache = window.__domCacheManager;
      const container = domCache?.getCachedElement("#professions-list", documentRef) 
        || documentRef.getElementById("professions-list");
      
      if (!container) {
        return;
      }

      container.innerHTML = "";

      Object.keys(professionsData).forEach((professionId) => {
        const profession = professionsData[professionId];
        const item = documentRef.createElement("div");
        item.className = "gift-item";
        item.style.marginBottom = "12px";
        item.innerHTML = `
          <div style="display: flex; align-items: center; gap: 12px;">
            <span style="font-size: 24px;">${profession.icon}</span>
            <div>
              <div style="font-weight: 600;">${profession.name}</div>
              <div style="font-size: 12px; color: var(--tg-theme-hint-color);">
                ${profession.benefits.join(" • ")}
              </div>
            </div>
          </div>
          <div style="font-size: 12px; color: var(--tg-theme-hint-color); margin-top: 8px;">
            <div class="stat-label">Профессия: ${profession.name}</div>
            <div style="font-size: 12px; color: var(--tg-theme-hint-color);">
              Характеристики: ${profession.stats
                .map((stat) => getProfessionStatLabel(stat))
                .join(", ")}
            </div>
          </div>
          <div style="margin-top: 12px;">
            <button class="btn profession-select-btn" data-creature-id="${creatureId}" data-profession-id="${professionId}" style="width: 100%;">
              Выбрать профессию
            </button>
          </div>
        `;
        container.appendChild(item);
      });

      // Удаляем старый обработчик, если он был добавлен ранее
      const oldHandler = container._professionClickHandler;
      if (oldHandler) {
        container.removeEventListener('click', oldHandler);
      }
      
      // Добавляем обработчик событий для кнопок выбора профессии
      const clickHandler = (e) => {
        const btn = e.target.closest('.profession-select-btn');
        if (btn) {
          const creatureId = parseInt(btn.dataset.creatureId);
          const professionId = btn.dataset.professionId;
          if (creatureId && professionId) {
            assignProfession(creatureId, professionId);
          } else {
            logger.error('[Professions] Missing data attributes');
          }
        }
      };
      
      container.addEventListener('click', clickHandler);
      container._professionClickHandler = clickHandler; // Сохраняем ссылку для последующего удаления

      const backButtonHTML = `
        <div style="margin-top: 16px;">
          <button class="btn btn-secondary" onclick="showScreen('creature-details')" style="width: 100%;">← Назад</button>
        </div>
      `;
      if (domCache) {
        const currentHTML = container.innerHTML;
        domCache.updateHTML("#professions-list", currentHTML + backButtonHTML, documentRef);
      } else {
        container.innerHTML += backButtonHTML;
      }
    } catch (error) {
      logger.error("Show professions error:", error);
      if (telegramWebApp?.showAlert) {
        telegramWebApp.showAlert(
          "Ошибка загрузки профессий: " + (error.message || "Неизвестная ошибка"),
        );
      }
    }
  }

  // Флаг для предотвращения множественных вызовов
  let isAssigningProfession = false;

  /**
   * Безопасный вызов showAlert с обработкой ошибки WebAppPopupOpened
   * showAlert может выбрасывать ошибку синхронно или через Promise rejection
   */
  function safeShowAlert(telegramWebApp, message) {
    if (!telegramWebApp?.showAlert) return;
    
    try {
      const result = telegramWebApp.showAlert(message);
      // Если showAlert возвращает Promise, обрабатываем rejection
      if (result && typeof result.catch === 'function') {
        result.catch((error) => {
          // Игнорируем ошибку, если popup уже открыт
          if (error?.message === 'WebAppPopupOpened' || error?.name === 'WebAppPopupOpened') {
            logger.warn('[Professions] Popup already opened, skipping:', message);
          } else {
            logger.error('[Professions] Error showing alert:', error);
          }
        });
      }
    } catch (error) {
      // Игнорируем ошибку, если popup уже открыт
      if (error?.message === 'WebAppPopupOpened' || error?.name === 'WebAppPopupOpened') {
        console.warn('[Professions] Popup already opened, skipping:', message);
      } else {
        console.error('[Professions] Error showing alert:', error);
      }
    }
  }

  /**
   * Назначает профессию существу
   */
  async function assignProfession(creatureId, professionId) {
    // Предотвращаем множественные вызовы
    if (isAssigningProfession) {
      console.warn('[Professions] Already assigning profession, skipping');
      return;
    }

    const telegramWebApp = getTelegramWebApp();
    isAssigningProfession = true;
    
    try {
      const apiUrl = getApiUrl();
      const response = await safeFetch(
        `${apiUrl}/creatures/${creatureId}/assign-profession`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...buildAuthHeaders(),
          },
          body: JSON.stringify({ professionId }),
        },
      );

      if (!response.success) {
        safeShowAlert(telegramWebApp, response.error || "Ошибка назначения профессии");
        return;
      }

      const data = response.data;
      if (data?.success || response.success) {
        // Показываем popup и только после этого переходим на другой экран
        safeShowAlert(telegramWebApp, "Профессия назначена! 💼");
        // Небольшая задержка перед переходом, чтобы popup успел показаться
        setTimeout(() => {
          showScreen("creature-details");
          // Если в ответе есть данные существа, используем их напрямую
          if (data?.creature && typeof window.renderCreatureDetails === "function") {
            window.renderCreatureDetails(data.creature, data.availableUpgradeMethods || []);
          } else if (typeof window.loadCreatureDetails === "function") {
            // Иначе загружаем данные заново
            setTimeout(() => {
              window.loadCreatureDetails({ creatureId });
            }, 200);
          }
        }, 100);
      } else {
        safeShowAlert(telegramWebApp, data?.message || "Ошибка назначения профессии");
      }
    } catch (error) {
      console.error("Assign profession error:", error);
      // Показываем ошибку только если это не ошибка popup
      if (error?.message !== 'WebAppPopupOpened' && error?.name !== 'WebAppPopupOpened') {
        safeShowAlert(telegramWebApp, "Ошибка назначения профессии");
      }
    } finally {
      // Сбрасываем флаг через небольшую задержку, чтобы избежать повторных вызовов
      setTimeout(() => {
        isAssigningProfession = false;
      }, 500);
    }
  }

  /**
   * Показывает экран улучшений для существа
   */
  async function showUpgrades(creatureId) {
    const telegramWebApp = getTelegramWebApp();
    try {
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/creatures/${creatureId}/full`, {
        headers: buildAuthHeaders(),
      });
      const data = await response.json();

      if (!data.success) {
        if (telegramWebApp?.showAlert) {
          telegramWebApp.showAlert("Ошибка загрузки данных существа");
        }
        return;
      }

      documentRef
        .querySelectorAll(".screen")
        .forEach((el) => el.classList.remove("active"));
      const screen = documentRef.getElementById("upgrades-screen");
      if (screen) {
        screen.classList.add("active");
      }

      const creature = data.creature;
      const emotionalStats = creature.emotional_stats || {};
      const statsContainer = documentRef.getElementById("stat-to-upgrade");
      if (statsContainer) {
        const statLabels = {
          happiness: "😊 Счастье",
          curiosity: "🔍 Любопытство",
          confidence: "💪 Уверенность",
          sociability: "💬 Общительность",
          empathy: "🤗 Эмпатия",
        };

        // Отображаем текущую энергию существа
        const currentEnergy = creature.energy !== undefined ? creature.energy : 100;
        const maxEnergy = 100;
        const energyDisplay = `
          <div style="padding: 8px 12px; background: rgba(59, 130, 246, 0.1); border-radius: 8px; margin-bottom: 12px;">
            <div style="display: flex; justify-content: space-between; align-items: center;">
              <span style="font-size: 14px; color: var(--tg-theme-text-color);">⚡ Энергия существа:</span>
              <span style="font-size: 16px; font-weight: 600; color: ${currentEnergy < 10 ? '#c62828' : currentEnergy < 30 ? '#f57c00' : 'var(--tg-theme-text-color)'};">
                ${currentEnergy}/${maxEnergy}
              </span>
            </div>
            ${creature.energyRecovery && creature.energyRecovery.nextRecoveryIn && currentEnergy < maxEnergy
              ? `<div style="font-size: 11px; color: var(--tg-theme-hint-color); margin-top: 4px;">
                   Восстановление через ${Math.ceil(creature.energyRecovery.nextRecoveryIn / 60)} мин
                 </div>`
              : ''
            }
          </div>
        `;

        statsContainer.innerHTML = `
          <div class="info-card">
            <h4>💪 Улучшение характеристик</h4>
            ${energyDisplay}
            <div class="stats-grid" style="margin-top: 8px;">
              ${Object.keys(emotionalStats)
                .map((stat) => {
                  const statData = emotionalStats[stat];
                  return `
                    <div class="stat-card" style="padding: 12px;">
                      <div class="stat-value" style="font-size: 18px;">${statData.value || 0}/${statData.max || 100}</div>
                      <div class="stat-label">${statLabels[stat] || stat}</div>
                    </div>
                  `;
                })
                .join("")}
            </div>
          </div>
        `;
      }

      const methodsContainer = documentRef.getElementById("upgrade-methods-list");
      if (!methodsContainer) {
        return;
      }

      methodsContainer.innerHTML = "";

      const cooldowns = data.cooldowns || {};
      const activeUpgrades = creature.activeUpgrades || [];
      const availableMethods = data.availableUpgradeMethods || [];

      if (availableMethods.length === 0) {
        methodsContainer.innerHTML =
          '<div class="info-card">Нет доступных методов улучшения</div>';
        return;
      }

      availableMethods.forEach((method) => {
        const item = documentRef.createElement("div");
        item.className = "gift-item";
        item.style.marginBottom = "12px";

        const costs = [];
        if (method.cost.coins) {
          costs.push(`🪙 ${method.cost.coins}`);
        }
        if (method.cost.stars) {
          costs.push(`⭐ ${method.cost.stars}`);
        }
        if (method.cost.energy) {
          costs.push(`⚡ ${method.cost.energy}`);
        }

        const effectText =
          method.effect.stat === "all"
            ? `+${method.effect.amount} ко всем характеристикам`
            : `+${method.effect.amount} к ${formatStatName(method.effect.stat)}`;

        const cooldown = cooldowns[method.id];
        const isOnCooldown = cooldown && cooldown.remainingSeconds > 0;

        let warningText = "";
        let isDisabled = false;
        let statusText = "";

        // Проверка невыполненных требований
        if (method.unmetRequirements && method.unmetRequirements.length > 0) {
          warningText = `<div style="font-size: 12px; color: #c62828; margin-top: 8px;">⚠️ Требуется: ${method.unmetRequirements.join(", ")}</div>`;
          isDisabled = true;
        }

        // Проверка энергии (если требуется)
        if (method.cost.energy && !isDisabled) {
          const creatureEnergy = creature.energy !== undefined ? creature.energy : 100;
          if (creatureEnergy < method.cost.energy) {
            warningText = `<div style="font-size: 12px; color: #c62828; margin-top: 8px;">⚡ Недостаточно энергии. Требуется: ${method.cost.energy}, доступно: ${creatureEnergy}</div>`;
            isDisabled = true;
          }
        }

        // Проверка активных улучшений
        if (
          activeUpgrades.some((upgrade) => upgrade.upgradeMethod === method.id) &&
          method.duration
        ) {
          const activeUpgrade = activeUpgrades.find(
            (upgrade) => upgrade.upgradeMethod === method.id,
          );
          const hours = Math.floor(activeUpgrade.remainingSeconds / 3600);
          const minutes = Math.floor(
            (activeUpgrade.remainingSeconds % 3600) / 60,
          );
          statusText = `<div style="font-size: 12px; color: var(--tg-theme-link-color); margin-top: 8px;">✨ Активно ещё ${hours > 0 ? `${hours}ч ${minutes}м` : `${minutes}м`}</div>`;
          isDisabled = true;
        }

        // Проверка перезарядки
        if (isOnCooldown) {
          const minutes = Math.floor(cooldown.remainingSeconds / 60);
          const seconds = cooldown.remainingSeconds % 60;
          statusText = `<div style="font-size: 12px; color: var(--tg-theme-hint-color); margin-top: 8px;">⏱️ Перезарядка: ${minutes}м ${seconds}с</div>`;
          isDisabled = true;
        } else if (method.cooldown) {
          statusText = `<div style="font-size: 12px; color: var(--tg-theme-hint-color); margin-top: 8px;">⏱️ Перезарядка: ${Math.floor(method.cooldown / 60)} мин</div>`;
        }

        item.innerHTML = `
          <div style="font-weight: 600; font-size: 16px;">${method.name}</div>
          <div style="font-size: 14px; color: var(--tg-theme-hint-color); margin-top: 4px;">
            ${effectText}
          </div>
          <div style="font-size: 12px; color: var(--tg-theme-link-color); margin-top: 8px;">
            ${costs.join(" ")}
          </div>
          ${warningText}
          ${statusText}
          <div style="margin-top: 12px;">
            <button class="btn" onclick="applyUpgrade(${creatureId}, '${method.effect.stat}', '${method.id}')" ${isDisabled ? "disabled" : ""} style="width: 100%;">
              ${isDisabled
                ? method.unmetRequirements && method.unmetRequirements.length > 0
                  ? "⚠️ Недоступно"
                  : "⏱️ На перезарядке"
                : "Применить"}
            </button>
          </div>
        `;
        methodsContainer.appendChild(item);
      });
    } catch (error) {
      console.error("Load upgrades error:", error);
      if (telegramWebApp?.showAlert) {
        telegramWebApp.showAlert("Ошибка загрузки улучшений");
      }
    }
  }

  /**
   * Применяет улучшение к существу
   */
  async function applyUpgrade(creatureId, statName, methodId) {
    const telegramWebApp = getTelegramWebApp();
    try {
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/creatures/${creatureId}/upgrade-stat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...buildAuthHeaders(),
        },
        body: JSON.stringify({ statName, methodId }),
      });

      const data = await response.json();

      if (data.success) {
        const upgrade = data.upgrade;
        if (telegramWebApp?.showAlert) {
          telegramWebApp.showAlert(
            `✅ Улучшение применено!\n${formatStatName(statName)}: ${upgrade.oldValue} → ${upgrade.newValue} (+${upgrade.improvement})`,
          );
        }

        if (data.userData) {
          const currentUser = getCurrentUser();
          if (currentUser) {
            const updatedUser = { ...currentUser, ...data.userData };
            setCurrentUser(updatedUser);
            updateUserBalancesUI(updatedUser);
          }
        }

        // Перезагружаем экран улучшений
        await showUpgrades(creatureId);
      } else {
        let errorMessage = data.message || "Ошибка применения улучшения";
        if (data.error === "insufficient_coins") {
          // Извлекаем точные значения из сообщения об ошибке
          const message = data.message || "";
          const requiredMatch = message.match(/Требуется:\s*(\d+)/);
          const currentMatch = message.match(/у вас:\s*(\d+)/);
          if (requiredMatch && currentMatch) {
            errorMessage = `Недостаточно монет. Требуется: ${requiredMatch[1]}, у вас: ${currentMatch[1]}`;
          } else {
            errorMessage = "Недостаточно монет. Требуется больше монет для этого улучшения.";
          }
        } else if (data.error === "insufficient_stars") {
          // Извлекаем точные значения из сообщения об ошибке
          const message = data.message || "";
          const requiredMatch = message.match(/Требуется:\s*(\d+)/);
          const currentMatch = message.match(/у вас:\s*(\d+)/);
          if (requiredMatch && currentMatch) {
            errorMessage = `Недостаточно звёзд. Требуется: ${requiredMatch[1]} ⭐, у вас: ${currentMatch[1]} ⭐`;
          } else {
            errorMessage = "Недостаточно звёзд. Требуется больше звёзд для этого улучшения.";
          }
        } else if (data.error === "insufficient_energy") {
          // Извлекаем точные значения из сообщения об ошибке
          const message = data.message || "";
          const requiredMatch = message.match(/Требуется:\s*(\d+)/);
          const currentMatch = message.match(/у существа:\s*(\d+)/);
          if (requiredMatch && currentMatch) {
            errorMessage = `Недостаточно энергии. Требуется: ${requiredMatch[1]}, у существа: ${currentMatch[1]}`;
          } else {
            errorMessage = "Недостаточно энергии у существа. Подождите восстановления энергии.";
          }
        } else if (data.error === "stat_maxed") {
          errorMessage = data.message || "Характеристика уже достигла максимума. Дальнейшее улучшение невозможно.";
        }

        if (telegramWebApp?.showAlert) {
          telegramWebApp.showAlert(errorMessage);
        }
      }
    } catch (error) {
      console.error("Apply upgrade error:", error);
      if (telegramWebApp?.showAlert) {
        telegramWebApp.showAlert(
          "Ошибка применения улучшения: " + (error.message || "Неизвестная ошибка"),
        );
      }
    }
  }

  /**
   * Показывает историю улучшений существа
   */
  async function showUpgradeHistory(creatureId) {
    const telegramWebApp = getTelegramWebApp();
    try {
      const apiUrl = getApiUrl();
      const response = await fetch(
        `${apiUrl}/creatures/${creatureId}/upgrade-history?limit=50`,
        {
          headers: buildAuthHeaders(),
        },
      );
      const data = await response.json();

      if (!data.success) {
        if (telegramWebApp?.showAlert) {
          telegramWebApp.showAlert("Ошибка загрузки истории улучшений");
        }
        return;
      }

      documentRef
        .querySelectorAll(".screen")
        .forEach((el) => el.classList.remove("active"));
      const screen = documentRef.getElementById("upgrade-history-screen");
      if (screen) {
        screen.classList.add("active");
      }

      const container = documentRef.getElementById("upgrade-history-list");
      if (!container) {
        return;
      }

      if (data.history.length === 0) {
        container.innerHTML =
          '<div class="loading">История улучшений пуста</div>';
        return;
      }

      const upgradeMethods = getUpgradeMethods?.() || {};
      const historyHtml = data.history
        .map((entry) => {
          const method =
            upgradeMethods[entry.upgradeMethod] || { name: entry.upgradeMethod };
          const costs = [];
          if (entry.cost.coins) {
            costs.push(`🪙 ${entry.cost.coins}`);
          }
          if (entry.cost.stars) {
            costs.push(`⭐ ${entry.cost.stars}`);
          }
          if (entry.cost.energy) {
            costs.push(`⚡ ${entry.cost.energy}`);
          }

          const date = new Date(entry.createdAt).toLocaleDateString("ru-RU", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });

          return `
            <div class="gift-item" style="margin-bottom: 12px;">
              <div style="font-weight: 600;">${method.name}</div>
              <div style="font-size: 14px; color: var(--tg-theme-hint-color); margin-top: 4px;">
                ${formatStatName(entry.statName)}: ${entry.oldValue} → ${entry.newValue} (+${entry.improvement})
              </div>
              <div style="font-size: 12px; color: var(--tg-theme-hint-color); margin-top: 8px;">
                ${costs.length > 0 ? costs.join(" ") + " • " : ""}${date}
              </div>
            </div>
          `;
        })
        .join("");

      container.innerHTML = historyHtml;
    } catch (error) {
      console.error("Show upgrade history error:", error);
      if (telegramWebApp?.showAlert) {
        telegramWebApp.showAlert("Ошибка загрузки истории улучшений");
      }
    }
  }

  return {
    showProfessions,
    assignProfession,
    showUpgrades,
    applyUpgrade,
    showUpgradeHistory,
  };
}

